import { Dropdown } from 'react-bootstrap';
import React from 'react';
import 'components/ui/dropdown/DropdownButton/DropdownButton.css';
import _, { isString } from 'lodash';
import clsx from 'clsx';

export interface BlazeDropdownItem {
  content: string | React.ReactNode;
  fnCallback?: () => void;
  /** If an item is heading one, it will be rendered as hierarchical. */
  isHeading?: boolean;
  disabled?: boolean;
  title?: string;
  id?: string;
}

export type DropdownPlacement = 'LEFT' | 'RIGHT';

interface DropdownButtonProps {
  /** Menu items */
  dropdownItems: BlazeDropdownItem[];
  dropdownToggleClassName?: string;
  innerButtonSpanClassName?: string;

  /** Text used in default toggle (if no custom toggle is provided) */
  toggleText?: string;

  /** Tooltip shown on hover event on the toggle. */
  toggleTooltip: string;
  disabled?: boolean;

  /** Custom toggle component can be provided.*/
  customToggle?: any;

  /** Changes display a type of the dropdown from block. */
  inline?: boolean;

  /** Controls menu position alignment from dropdown toggle. */
  dropDownPlacement?: DropdownPlacement;

  /** Optional heading component */
  dropdownHeader?: any;
}

const convertStringToIdFormat = (stringToConvert: string | any): string => {
  if (isString(stringToConvert) === false) {
    return '';
  }
  return stringToConvert.toLocaleLowerCase().replace(/\s+/g, '-');
};

/**
 * Custom builder for the custom Toggle in DropdownButton.
 * @param props
 */
export const buildDropdownButtonToggle = (props: { title: string; toggleComponent: any; disabled?: boolean }) => {
  const { disabled, title, toggleComponent } = props;
  // eslint-disable-next-line react/display-name
  return React.forwardRef(({ onClick }: any, ref: any) => (
    <div
      onClick={e => {
        e.preventDefault();
        if (disabled) {
          return;
        }
        onClick(e);
      }}
      ref={ref}
      className="d-inline-block hand-cursor"
      title={title}
    >
      {toggleComponent}
    </div>
  ));
};

const DropdownButton = (props: DropdownButtonProps) => {
  const {
    dropdownItems,
    toggleTooltip,
    innerButtonSpanClassName,
    dropdownToggleClassName,
    toggleText,
    customToggle,
  } = props;
  const dropdownCaretClassName = ' caret-off ';

  const getDropdownItems = () => {
    //If there is any heading element, we need to create indentation
    const hasHeading = _.some(dropdownItems, item => _.has(item, 'isHeading'));
    const items = dropdownItems.map((item: BlazeDropdownItem, index: number) => {
      const className = hasHeading
        ? item.isHeading === true
          ? 'dropdown-item-text'
          : 'dropdown-item dropdown-item-nested-1'
        : undefined;

      if (hasHeading && item.isHeading === true) {
        return (
          <div key={0} className={'dropdown-item-text'}>
            <strong>{item.content}</strong>
          </div>
        );
      }

      return (
        <Dropdown.Item
          id={`qa-dropdown-item-${convertStringToIdFormat(item.content)}`}
          className={clsx('title-enabled', className)}
          disabled={item.disabled}
          key={index}
          title={item.title}
          eventKey={index}
          onClick={item.fnCallback}
        >
          {item.isHeading === true ? <strong>{item.content}</strong> : item.content}
        </Dropdown.Item>
      );
    });
    const dropdownHeader = props.dropdownHeader && <div className={'dropdown-header'}>{props.dropdownHeader}</div>;
    return (
      <>
        {dropdownHeader}
        {items}
      </>
    );
  };

  const isAlignedRight = () => {
    if (props.dropDownPlacement === 'RIGHT') {
      return true;
    } else if (props.dropDownPlacement === 'LEFT') {
      return false;
    }
    return true;
  };

  return (
    <Dropdown style={{ display: props.inline ? 'inline-block' : undefined }}>
      {customToggle ? (
        <Dropdown.Toggle as={customToggle}></Dropdown.Toggle>
      ) : (
        <Dropdown.Toggle
          disabled={props.disabled}
          id={`blaze-dropdown-${convertStringToIdFormat(toggleTooltip)}`}
          className={clsx(dropdownToggleClassName, dropdownCaretClassName)}
          title={toggleTooltip}
        >
          <span className={innerButtonSpanClassName} aria-hidden="true">
            {toggleText}
          </span>
        </Dropdown.Toggle>
      )}
      <Dropdown.Menu alignRight={isAlignedRight()}>{getDropdownItems()}</Dropdown.Menu>
    </Dropdown>
  );
};

export default DropdownButton;
